import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HubConnectionState } from "@microsoft/signalr";

import { calculateDisplayedProgress } from "../../../models/functions/calculate-displayed-progress.function";
import { keyOfType } from "../../../models/functions/key-of-type.function";
import { IFileProgress } from "../../../models/interfaces";
import { IAsyncConnection } from "../AsyncConnections/async.connection.interface";
import { SignalRConnection } from "../AsyncConnections/SignalRConnection";
import { ICustomProgressColumn, IProgressColumn } from "./progressHandler.types";

interface IUseProgressHandlerOptions {
  progresscolumns: (IProgressColumn | ICustomProgressColumn)[];
  cancelProgress: () => void;
  connectionSetupCompleted: () => void;
  enabled?: boolean;
  stopConnectionsOnUnmount?: boolean;
}

interface IUseProgressHandlerResult {
  progressTitles: string[];
  progressValues: number[];
  cancelProgress: () => void;
}

export const useProgressHandler = ({
  progresscolumns,
  cancelProgress: onCancelProgress,
  connectionSetupCompleted,
  enabled = true,
  stopConnectionsOnUnmount = true,
}: IUseProgressHandlerOptions): IUseProgressHandlerResult => {
  const connectionsRef = useRef<IAsyncConnection[]>([]);
  const intervalsRef = useRef<ReturnType<typeof setInterval>[]>([]);

  const actualProgressColumnsRef = useRef<number[]>([]);
  const [displayProgressColumns, setDisplayProgressColumns] = useState<number[]>(() =>
    new Array(progresscolumns.length).fill(0),
  );

  const updateDisplayProgressColumns = (index: number, progress: number) => {
    if (!displayProgressColumns?.length) {
      const array = new Array(progresscolumns.length).fill(0);
      array[index] = progress;
      setDisplayProgressColumns(array);
      return;
    }

    setDisplayProgressColumns(prevDisplayProgress => {
      const updatedDisplayProgress = [...prevDisplayProgress];
      updatedDisplayProgress[index] = progress;
      return updatedDisplayProgress;
    });
  };

  const updateDisplayProgressWithInterval = (actualProgressColumns: number[]) => {
    intervalsRef.current.forEach(interval => interval && clearInterval(interval));
    intervalsRef.current = [];

    actualProgressColumns.forEach((actualProgress: number, index: number) => {
      const tempDisplayProgress = displayProgressColumns;

      if (actualProgress > 0 && tempDisplayProgress[index] < 100 && actualProgress !== tempDisplayProgress[index]) {
        const interval = setInterval(() => {
          const oldDisplayProgress = tempDisplayProgress[index];

          const newDisplayProgress = calculateDisplayedProgress(oldDisplayProgress, actualProgress);

          updateDisplayProgressColumns(index, newDisplayProgress);

          if (oldDisplayProgress === newDisplayProgress) {
            clearInterval(interval);
          }

          tempDisplayProgress[index] = newDisplayProgress;
        }, 100);

        intervalsRef.current.push(interval);
      }
    });
  };

  const updateActualProgressColumns = useCallback(
    (index: number, progress: number) => {
      if (!actualProgressColumnsRef.current?.length) {
        actualProgressColumnsRef.current = new Array(progresscolumns.length).fill(0);
      }
      if (actualProgressColumnsRef.current[index] === progress) {
        return;
      }

      const current = [...actualProgressColumnsRef.current];
      current[index] = progress;
      actualProgressColumnsRef.current = [...current];

      updateDisplayProgressWithInterval(actualProgressColumnsRef.current);
    },
    [progresscolumns.length],
  );

  const progressCallback = (index: number, progress: number) => {
    updateActualProgressColumns(index, progress);
  };

  const isCustomProgressColumn = (progressColumn: any): progressColumn is ICustomProgressColumn => {
    return keyOfType<ICustomProgressColumn>("setupConnections") in progressColumn;
  };

  const isProgressColumn = (progressColumn: any): progressColumn is IProgressColumn => {
    return keyOfType<IProgressColumn>("hubName") in progressColumn;
  };

  const stopConnection = (openConnection: IAsyncConnection) => {
    if (openConnection && openConnection.state === HubConnectionState.Connected) {
      openConnection.stop();
    }
  };

  const stopConnections = (openConnections: IAsyncConnection[] = connectionsRef.current) => {
    if (openConnections.length) {
      openConnections.forEach(connection => {
        stopConnection(connection);
      });
    }
  };

  const completeAllPreviousColumns = useCallback(
    (currentIndex: number, localConnections: IAsyncConnection[]) => {
      for (let index = currentIndex - 1; index >= 0; index--) {
        const column = progresscolumns[index];
        if (column) {
          updateActualProgressColumns(index, 100);
          updateDisplayProgressColumns(index, 100);
        }

        if (isProgressColumn(column) && localConnections[index].state === HubConnectionState.Connected) {
          stopConnection(localConnections[index]);
        }
      }
    },
    [progresscolumns],
  );

  const cancelProgress = () => {
    stopConnections();
    onCancelProgress();
  };

  useEffect(() => {
    if (!enabled) {
      actualProgressColumnsRef.current = [];
      setDisplayProgressColumns(new Array(progresscolumns.length).fill(0));
      stopConnections();
      return;
    }

    setDisplayProgressColumns(new Array(progresscolumns.length).fill(0));
    actualProgressColumnsRef.current = new Array(progresscolumns.length).fill(0);
  }, [enabled, progresscolumns.length]);

  useEffect(() => {
    if (!enabled || !progresscolumns.length) {
      return;
    }

    let isCancelled = false;
    const localConnections: IAsyncConnection[] = [];

    const startCustomConnection = async (column: ICustomProgressColumn, index: number): Promise<void> => {
      if (column.setupConnections) {
        const connection = column.setupConnections(progressCallback.bind(this, index));
        localConnections.push(connection);
        if (isCancelled && stopConnectionsOnUnmount) {
          connection.stop();
        }
      }
    };

    const startSignalRConnection = async (column: IProgressColumn, index: number): Promise<void> => {
      const signalRConnection = new SignalRConnection(`${column.hubName}hub`, ...column.filter);
      localConnections.push(signalRConnection);

      const progressEndpoint = column.progressEndpoint;
      const successEndpoint = column.successEndpoint;
      const errorEndpoint = column.errorEndpoint;

      signalRConnection.on(progressEndpoint, (fileProgress: IFileProgress) => {
        console.debug(`Progress for Hub ${progressEndpoint.toUpperCase()}:`, fileProgress.progress);

        if (isCancelled) {
          return;
        }

        updateActualProgressColumns(index, fileProgress.progress);

        if (fileProgress.progress > 0 && actualProgressColumnsRef.current[-1] !== 100) {
          completeAllPreviousColumns(index, localConnections);
        }

        if (column.progressCallback) {
          column.progressCallback(fileProgress.progress);
        }
      });

      signalRConnection.on(successEndpoint, (res: any) => {
        console.debug(`Success for Hub ${successEndpoint.toUpperCase()}:`, res);

        if (!isCancelled) {
          updateActualProgressColumns(index, 100);
          completeAllPreviousColumns(index, localConnections);
        }

        signalRConnection.stop();

        column.successCallback(res);
      });

      signalRConnection.on(errorEndpoint, (res: any) => {
        console.debug(`Error for Hub ${errorEndpoint.toUpperCase()}:`, res);
        column.errorCallback(res);
      });

      await signalRConnection.start();
      if (isCancelled && stopConnectionsOnUnmount) {
        await signalRConnection.stop();
      }
    };

    const processColumns = async () => {
      for (let i = 0; i < progresscolumns.length; i++) {
        const column = progresscolumns[i];

        if (isCustomProgressColumn(column)) {
          await startCustomConnection(column, i);
        } else if (isProgressColumn(column)) {
          await startSignalRConnection(column, i);
        }
      }

      connectionsRef.current = localConnections;

      connectionSetupCompleted();
    };

    if (!localConnections.length) {
      processColumns();
    }

    return () => {
      isCancelled = true;
      if (stopConnectionsOnUnmount) {
        stopConnections(localConnections);
      }
    };
  }, [enabled, progresscolumns, stopConnectionsOnUnmount]);

  const progressTitles = useMemo(() => progresscolumns.map(column => column.titleKey), [progresscolumns]);

  return {
    progressTitles,
    progressValues: displayProgressColumns,
    cancelProgress,
  };
};
